import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import TimelineMax from 'TimelineMax';
import TweenMax from 'TweenMax';
import { inject, observer } from 'mobx-react';

import {
  toVw,
  verticalAlign,
  verticalAlignWrapper,
  toVwMobile,
  toVwTablet,
  transitionDefault,
  link,
  linkChangeColor,
} from '../utils/styles';
import { FONT_SIZE_LARGE, PREVIEW_TYPES } from '../constants';
import 'ScrollToPlugin';
import mq from '@/utils/media-queries';
import Container from './Container';
import Media from './Media';
import LazyLoad from 'react-lazyload';
import InvertingColorsBlock from './InvertingColorsBlock';

const Styled = styled.div`
  display: block;
  position: relative;
  height: 100vh;
  text-align: center;
  width: calc(100% + 2px);
  left: -1px;

  ${props =>
    props.hex &&
    !props.animationRunning &&
    css`
      background-color: ${colorHex};
    `};

  @media screen and (orientation: portrait) {
    height: 100vw;
  }
`;

const StyledLink = Styled.withComponent('a');

const Wrapper = props => (props.href ? <StyledLink {...props} /> : <Styled {...props} />);
Wrapper.propTypes = {
  href: PropTypes.string,
};

const Inner = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  will-change: transform;
  z-index: 1;
  overflow: hidden;

  ${props =>
    props.darken &&
    css`
      &:after {
        content: '';
        display: block;
        position: absolute;
        width: 100%;
        height: 100%;
        left: 0;
        top: 0;
        background-color: rgba(0, 0, 0, 0.2);
        ${transitionDefault};
      }
    `};

  ${props =>
    props.animationRunning &&
    css`
      &:after {
        opacity: 0;
      }
    `};
`;

const ImageMedia = styled(Media)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: auto;

  img {
    transform: scale(1.05);
    will-change: transform;
  }

  ${props =>
    props.blockType === PREVIEW_TYPES.page &&
    css`
      height: 100%;

      img {
        object-fit: cover;
        object-position: 50% 50%;
        font-family: 'object-fit: cover;';
      }
    `};
`;

const Content = styled.div`
  text-align: center;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  ${verticalAlignWrapper};
`;

const ContentInner = styled(Container)`
  ${verticalAlign};
`;

const baseText = css`
  font-size: ${toVw(FONT_SIZE_LARGE)};

  ${props =>
    props.type === PREVIEW_TYPES.case &&
    css`
      font-size: ${toVw(64)};
    `};

  ${mq.tablet(css`
    font-size: ${toVwTablet(64)};
  `)};

  ${mq.mobile(css`
    font-size: ${toVwMobile(64)};
  `)};
`;

const StyledTitle = styled.span`
  ${baseText};
  color: #fff;
  text-decoration: none;

  ${props =>
    props.type === PREVIEW_TYPES.page &&
    css`
      ${link};
      ${linkChangeColor('#fff')};
    `};
`;

const TitleLink = StyledTitle.withComponent('a');

const Title = props => (props.href ? <TitleLink {...props} /> : <StyledTitle {...props} />);
Title.propTypes = {
  href: PropTypes.string,
};

const Subtitle = styled.div`
  ${baseText};
  color: rgba(255, 255, 255, 0.5);
`;

class PreviewNext extends React.Component {
  static propTypes = {
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    image: PropTypes.object.isRequired,
    to: PropTypes.string.isRequired,
    type: PropTypes.oneOf([PREVIEW_TYPES.case, PREVIEW_TYPES.page]),
    onLinkClick: PropTypes.func.isRequired,
    animationRunning: PropTypes.bool,
    hover: PropTypes.bool,
    containerNode: PropTypes.func.isRequired,
    contentNode: PropTypes.func,
    imageNode: PropTypes.func.isRequired,
    onAnimationStart: PropTypes.func,
    onMouseOver: PropTypes.func.isRequired,
    onMouseLeave: PropTypes.func.isRequired,
    isDarkTheme: PropTypes.bool,
    themeStore: PropTypes.object.isRequired,
    hex: PropTypes.string,
  };

  componentDidUpdate(prevProps) {
    if (prevProps.hover !== this.props.hover) {
      if (this.props.hover) {
        const tl = new TimelineMax();
        tl.to(this.imageNode, 0.25, { scale: 1.03 });
        tl.to(this.imageNode, 10, { scale: 1 });
      } else if (!this.props.animationRunning) {
        TweenMax.to(this.imageNode, 0.25, { scale: 1.05 });
      }
    }
  }

  setImageRef = node => {
    this.imageNode = node;
    this.props.imageNode(node);
  };

  handleLinkClick = e => {
    e.preventDefault();
    if (this.props.onAnimationStart) {
      this.props.onAnimationStart();
    }
    this.props.onLinkClick(this.props.to);
  };

  setContentNode = node => {
    if (this.props.contentNode) {
      this.props.contentNode(node);
    }
  };

  render() {
    const {
      title,
      subtitle,
      to,
      type,
      image,
      hover,
      isDarkTheme,
      themeStore,
      hex,
      containerNode,
      onMouseOver,
      onMouseLeave,
      animationRunning,
    } = this.props;
    const typeIsCase = type === PREVIEW_TYPES.case;

    if (!to) {
      return null;
    }

    const shouldInvert = (themeStore.isDark && !isDarkTheme) || (!themeStore.isDark && isDarkTheme);

    return (
      <LazyLoad offset={4000} once>
        <InvertingColorsBlock shouldInvert={shouldInvert} noFirstCall name={'BOTTOM'}>
          <Wrapper
            href={typeIsCase ? to : undefined}
            onClick={typeIsCase ? this.handleLinkClick : undefined}
            data-test="preview-next"
            hexColor={hex}
            animationRunning={animationRunning}
          >
            <Inner innerRef={containerNode} darken={typeIsCase} animationRunning={animationRunning}>
              <ImageMedia
                type={'image'}
                content={image}
                mediaRef={this.setImageRef}
                hover={hover}
                blockType={type}
                fullHeight={!typeIsCase}
                noRatio={!typeIsCase}
              />
            </Inner>
            <Content
              innerRef={this.setContentNode}
              onMouseEnter={typeIsCase ? onMouseOver : undefined}
              onMouseLeave={typeIsCase ? onMouseLeave : undefined}
            >
              <ContentInner>
                <Title
                  href={typeIsCase ? undefined : to}
                  color="#fff"
                  type={type}
                  onMouseEnter={typeIsCase ? undefined : onMouseOver}
                  onMouseLeave={typeIsCase ? undefined : onMouseLeave}
                  onClick={typeIsCase ? undefined : this.handleLinkClick}
                >
                  {title}
                </Title>
                {subtitle && (
                  <div>
                    <Subtitle type={type}>{subtitle}</Subtitle>
                  </div>
                )}
              </ContentInner>
            </Content>
          </Wrapper>
        </InvertingColorsBlock>
      </LazyLoad>
    );
  }
}

export default inject(stores => ({ themeStore: stores.store.theme }))(observer(PreviewNext));



// WEBPACK FOOTER //
// ./src/components/PreviewNext.js